/**
 * Spec 8 — colour identity is the accent language. Each deck's rating bar is a
 * gradient across its WUBRG identity, which makes standings scannable at a glance
 * and is grounded in the subject rather than decorative.
 */

import type { Color } from './types.js';
import { COLORS } from './types.js';

export const COLOR_HEX: Record<Color, string> = {
  W: '#F2E4C3',
  U: '#4E93D6',
  B: '#8C7BA8',
  R: '#D45B47',
  G: '#57A566',
};

export const COLORLESS_HEX = '#7A748F';

/** WUBRG order, the order every Magic player already reads in. */
export function sortColors(colors: readonly Color[]): Color[] {
  return COLORS.filter((c) => colors.includes(c));
}

/**
 * A CSS gradient for a colour identity. One colour still gets two stops so the
 * bar renders identically to a multicolour one rather than switching code paths.
 */
export function identityGradient(colors: readonly Color[], angle = '90deg'): string {
  const sorted = sortColors(colors);
  if (sorted.length === 0) return `linear-gradient(${angle}, ${COLORLESS_HEX}, ${COLORLESS_HEX})`;
  if (sorted.length === 1) {
    const hex = COLOR_HEX[sorted[0]!];
    return `linear-gradient(${angle}, ${hex}, ${hex})`;
  }
  const stops = sorted.map((c, i) => {
    const pct = (i / (sorted.length - 1)) * 100;
    return `${COLOR_HEX[c]} ${pct.toFixed(1)}%`;
  });
  return `linear-gradient(${angle}, ${stops.join(', ')})`;
}

/** Single representative colour, for borders and tints where a gradient is noise. */
export function identityAccent(colors: readonly Color[]): string {
  const sorted = sortColors(colors);
  if (sorted.length === 0) return COLORLESS_HEX;
  if (sorted.length === 1) return COLOR_HEX[sorted[0]!];
  return '#D4A63C'; // gold for multicolour, matching the app accent
}
